/**
 * Relationship condition renderer.
 * Matches ~/code/ui condition-editor-v2/nodes/relationship/view.gts
 *
 * Renders: Relationship to **objectName** exists/does not exist
 * with optional split sub-filters for object vs relationship attributes.
 * The first filter list's lead-in ends the sentence; subsequent lists carry
 * their lead-in as the detail list's label (per Pluma's ConditionViewer docs).
 */

import { ConditionViewerItem, ConditionViewerTerm } from '@customerio/pluma-components/react';
import type { RelationshipNode, ConditionFilter } from '../../types';
import { getIconForNode } from '../data-source-icon';
import { FilterDetailList, FiltersLeadIn, filtersLeadInText } from '../condition-parts';

function splitFilters(filters: ConditionFilter[] | undefined): {
  objectFilters: ConditionFilter[];
  relationshipFilters: ConditionFilter[];
} {
  if (filters == null || filters.length === 0) {
    return { objectFilters: [], relationshipFilters: [] };
  }

  const objectFilters: ConditionFilter[] = [];
  const relationshipFilters: ConditionFilter[] = [];

  for (const filter of filters) {
    if (filter.sourceType === 'object') {
      objectFilters.push(filter);
    } else {
      relationshipFilters.push(filter);
    }
  }

  return { objectFilters, relationshipFilters };
}

type RelationshipConditionProps = {
  node: RelationshipNode;
};

export function RelationshipCondition({ node }: RelationshipConditionProps) {
  const name = node.objectTypeName ?? (node.objectTypeId != null ? `object #${node.objectTypeId}` : 'object');
  const { objectFilters, relationshipFilters } = splitFilters(node.filters);

  const lists: Array<{ filters: ConditionFilter[]; label: string }> = [];
  if (objectFilters.length > 0) lists.push({ filters: objectFilters, label: `${name} attributes` });
  if (relationshipFilters.length > 0) lists.push({ filters: relationshipFilters, label: 'relationship attributes' });
  const [firstList, ...restLists] = lists;

  return (
    <ConditionViewerItem
      icon={getIconForNode(node)}
      details={
        firstList == null ? undefined : (
          <>
            <FilterDetailList filters={firstList.filters} />
            {restLists.map((list) => (
              <FilterDetailList
                key={list.label}
                filters={list.filters}
                label={filtersLeadInText(node.filtersOperator, list.label)}
              />
            ))}
          </>
        )
      }
    >
      Relationship to <ConditionViewerTerm>{name}</ConditionViewerTerm> {node.inverse ? 'does not exist' : 'exists'}
      {firstList == null ? null : (
        <FiltersLeadIn filters={firstList.filters} filtersOperator={node.filtersOperator} label={firstList.label} />
      )}
    </ConditionViewerItem>
  );
}
